import { converObjectErrorInString, convertObjectToCamelCase, convertObjectToSnakeCase } from '@/helpers'
import { useMutation, useQuery } from '@tanstack/react-query'

import { AddressListWithPagesProps, AddressProps } from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import {
  ADDRESSES_URL,
  ADD_ADDRESS_URL,
  BFF_USERS_ADDRESSES_URL,
  BFF_USERS_ADDRESS_URL,
} from './api.urls'

export const useAddressList = (profileUuid: string) => {
  const getAddressList = async (): Promise<{
    success: boolean
    message: string
    data?: { addresses: AddressProps[] }
  }> => {
    try {
      const { data } = await api.get(ADDRESSES_URL(profileUuid))

      return {
        success: true,
        message: 'Address requests fetched successfully!',
        data: convertObjectToCamelCase(data) as { addresses: AddressProps[] },
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get adresses!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.ADDRESS.module, profileUuid],
    queryFn: () => getAddressList(),
    enabled: !!profileUuid,
  })
}

export const useCreateAddress = (profileUuid: string) => {
  const createAddress = async (
    payload: Partial<AddressProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: AddressProps
  }> => {
    try {
      const { data } = await api.post(
        ADD_ADDRESS_URL(profileUuid),
        convertObjectToSnakeCase(payload),
      )

      return {
        success: true,
        message: 'Address created successfully!',
        data: convertObjectToCamelCase(data) as AddressProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create address!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.ADDRESS.module],
    mutationFn: (payload: Partial<AddressProps>) => createAddress(payload),
  })
}

export const useBffCreateAddress = () => {
  const createAddress = async (
    payload: Partial<AddressProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: AddressProps
  }> => {
    try {
      const { data } = await api.post(BFF_USERS_ADDRESSES_URL, payload)

      return {
        success: true,
        message: 'Address created successfully!',
        data: convertObjectToCamelCase(data) as AddressProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to create address!',
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_ADDRESS.module],
    mutationFn: (payload: Partial<AddressProps>) => createAddress(payload),
  })
}

export const useBffAddressList = (
  params: Record<string, string | number>,
  isEnabled = true,
) => {
  const getAddressList = async (): Promise<{
    success: boolean
    message: string
    data?: AddressListWithPagesProps
  }> => {
    try {
      const copyParams = { ...params }

      const offset = copyParams?.page
        ? (+(copyParams?.page || 0) - 1) * +copyParams.limit
        : 0

      delete copyParams.page

      const formattedParams = Object.keys(copyParams).reduce((acc, key) => {
        if (copyParams?.[key] && copyParams?.[key] !== 'undefined') {
          return {
            ...acc,
            [key]: copyParams?.[key],
          }
        }

        return acc
      }, {})

      const { data } = await api.get(BFF_USERS_ADDRESSES_URL, {
        params: {
          ...formattedParams,
          offset,
        },
      })

      return {
        success: true,
        message: 'Address requests fetched successfully!',
        data: convertObjectToCamelCase(data) as AddressListWithPagesProps,
      }
    } catch (err) {
      return {
        success: false,
        message: 'Failed to get adresses!',
        data: undefined,
      }
    }
  }

  return useQuery({
    queryKey: [QUERIES_ADMIN.BFF_ADDRESS.module, params],
    queryFn: () => getAddressList(),
    enabled: !!isEnabled,
  })
}

export const useEditAddress = (uuid: string) => {
  const editAddress = async (
    payload: Partial<AddressProps>,
  ): Promise<{
    success: boolean
    message: string
    data?: AddressProps
  }> => {
    try {
      const { data } = await api.patch(BFF_USERS_ADDRESS_URL(uuid), payload)

      return {
        success: true,
        message: 'Address edited successfully!',
        data: convertObjectToCamelCase(data) as AddressProps,
      }
    } catch (err: any) {
      let message = ''

      if (err?.response?.data) {
        message = converObjectErrorInString(err?.response?.data) || message
      }
      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  return useMutation({
    mutationKey: [QUERIES_ADMIN.BFF_ADDRESS.module],
    mutationFn: (payload: Partial<AddressProps>) => editAddress(payload),
  })
}
